import { sortBy } from 'lodash';
import type { MeetingOccurrenceViewModelReminderEnum } from '@trinnvis/dabih-api-client';
import { LocalDate } from 'src/utilities/local-date';
import { LocalTime } from 'src/utilities/local-time';
import { capitalize, convertToMinutes, formatDuration } from 'src/utilities/text';
import { reminderOptions } from './data/reminder-options';

function compareByLabel(
  a: { label?: string | null } | null | undefined,
  b: { label?: string | null } | null | undefined,
): number {
  const an = (a || {}).label ?? '';
  const bn = (b || {}).label ?? '';
  return an.toLowerCase().localeCompare(bn.toLowerCase());
}

export function sorted<T>(list: Array<T>, sortBy: (a: T, b: T) => number): Array<T> {
  return [...list].sort(sortBy);
}

export function sortedByName<T extends { name?: string | null }>(list: Array<T>): Array<T> {
  return sortBy(list, [(item) => item.name]);
}

export function sortedByLabel<T extends { label?: string | null }>(list: Array<T>): Array<T> {
  return sorted(list, compareByLabel);
}

export function sortedByNumberAndName<T extends { number: string; name: string | null }>(list: Array<T>): Array<T> {
  return sortBy(list, [(item) => item.number, (item) => item.name]);
}

export function reminderText(reminder: MeetingOccurrenceViewModelReminderEnum): string {
  const r = reminderOptions.find((x) => x.value === reminder);
  return r?.text ?? '';
}

export function timeDescription(time: string, duration: number = 0, prefix: string = '') {
  if (time && time !== 'NONE') {
    let result = formattedTime(time, prefix);
    if (duration > 0) {
      result += '-' + formatDuration(convertToMinutes(time) + duration).replace(':', '.');
    }
    return result;
  }
  return '';
}

export function dateTimeDescription(date: string | undefined, time: string | undefined, duration: number = 0) {
  if (!date) return 'Snarest';
  const s = LocalDate.fromString(date);
  let result = s.toStringForDisplayWithDayOfWeekAndYear();
  if (time && time !== 'NONE') {
    result += ' ' + timeDescription(time, duration, ' kl. ');
  }
  return capitalize(result);
}

export function formattedTime(time: string, prefix: string): string {
  if (time === '') {
    return '';
  } else {
    return prefix + LocalTime.fromString(time).toStringForDisplay();
  }
}

export function toDateTime(date: string, time: string | undefined | null) {
  return `${date} ${time ?? ''}`.trim();
}

export function dateTimeDisplayShort(date: string | undefined, time: string | undefined): string {
  if (!date) {
    return 'Snarest';
  }
  let timeDisplay = '';
  if (time) {
    timeDisplay = ' ' + time.replace(':', '.');
  }
  return LocalDate.fromString(date).toStringForDisplay() + timeDisplay;
}

export function iconUrl(entityType: string, colorVariant: 'color' | 'gray'): string {
  if (!entityType) return '';
  // These icons are not entity type icons and only exist in gray version, without the '-gray' suffix
  const specificIcons = ['starttask', 'versions', 'attachments', 'references'];
  if (specificIcons.includes(entityType)) {
    return 'url(/images/' + entityType + '.svg)';
  }
  const typeName = entityType.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase());
  return 'url(/images/' + typeName + '-' + colorVariant + '.svg)';
}
